'use client';

import { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Mic, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from 'sonner';
import { AudioRecorder, transcribeAudio } from '@/services/transcription-service';

/**
 * Ref interface for external control of the DictationButton
 */
export interface DictationButtonRef {
  /**
   * Start recording audio
   */
  startRecording: () => Promise<void>;
  /**
   * Stop the current recording session and get the audio blob
   * @returns Promise resolving to the recorded audio blob, or null if not recording
   */
  stopRecording: () => Promise<Blob | null>;
  /**
   * Cancel the current recording session without returning data
   */
  cancelRecording: () => void;
  /**
   * Transcribe the recorded audio and return the text
   * @returns Promise resolving to the transcribed text
   */
  transcribeRecording: () => Promise<string>;
  /**
   * Check if currently recording
   */
  isRecording: boolean;
}

/**
 * Props for the DictationButton component
 */
export interface DictationButtonProps {
  /**
   * Callback when transcribed text is available (after transcription completes)
   */
  onTranscript?: (transcript: string) => void;
  /**
   * Callback when recording starts
   */
  onDictationStart?: () => void;
  /**
   * Callback when recording stops (normal stop, not error)
   */
  onDictationStop?: () => void;
  /**
   * Callback when an error occurs during recording or transcription
   * Parent should use this to reset recording state
   */
  onDictationError?: (errorMessage: string) => void;
  /**
   * Whether the button is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Language code for transcription (default: "en")
   */
  language?: string;
}

/**
 * Dictation button component using OpenAI Whisper for transcription
 *
 * Features:
 * - Audio recording using MediaRecorder API
 * - Transcription via backend Whisper API
 * - Visual feedback for recording state
 * - Error handling with user feedback
 * - Imperative handle for external control
 *
 * @param props - Component configuration
 * @param ref - Ref for external control (stopRecording, cancelRecording, transcribeRecording)
 */
export const DictationButton = forwardRef<DictationButtonRef, DictationButtonProps>(function DictationButton({
  onTranscript,
  onDictationStart,
  onDictationStop,
  onDictationError,
  disabled = false,
  className,
  language = 'en'
}, ref) {
  const [isRecording, setIsRecording] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const recorderRef = useRef<AudioRecorder | null>(null);
  const recordedBlobRef = useRef<Blob | null>(null);

  /**
   * Check if audio recording is supported
   */
  const isSupported = AudioRecorder.isSupported();

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async () => {
    if (!isSupported) {
      toast.error('Audio recording is not supported in this browser.');
      return;
    }

    if (disabled) {
      return;
    }

    try {
      setIsInitializing(true);

      // Create new recorder instance
      recorderRef.current = new AudioRecorder();
      await recorderRef.current.start();

      setIsRecording(true);
      setIsInitializing(false);
      recordedBlobRef.current = null;

      onDictationStart?.();
    } catch (error) {
      console.error('Failed to start recording:', error);
      setIsInitializing(false);
      setIsRecording(false);

      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to start recording. Please check your microphone permissions.';

      toast.error(errorMessage);
      onDictationError?.(errorMessage);
    }
  }, [isSupported, disabled, onDictationStart, onDictationError]);

  /**
   * Stop recording and return the audio blob
   */
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    if (!recorderRef.current || !isRecording) {
      return null;
    }

    try {
      const blob = await recorderRef.current.stop();
      recordedBlobRef.current = blob;
      setIsRecording(false);
      onDictationStop?.();
      return blob;
    } catch (error) {
      console.error('Failed to stop recording:', error);
      setIsRecording(false);

      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to stop recording.';

      toast.error(errorMessage);
      onDictationError?.(errorMessage);
      return null;
    }
  }, [isRecording, onDictationStop, onDictationError]);

  /**
   * Cancel recording without returning data
   */
  const cancelRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.cancel();
      recorderRef.current = null;
    }
    recordedBlobRef.current = null;
    setIsRecording(false);
    setIsInitializing(false);
    setIsTranscribing(false);
  }, []);

  /**
   * Transcribe the recorded audio
   */
  const transcribeRecording = useCallback(async (): Promise<string> => {
    // If still recording, stop first
    let blob = recordedBlobRef.current;
    if (isRecording && recorderRef.current) {
      blob = await stopRecording();
    }

    if (!blob) {
      throw new Error('No recorded audio to transcribe');
    }

    try {
      setIsTranscribing(true);

      const result = await transcribeAudio(blob, language);

      setIsTranscribing(false);

      if (result.text) {
        onTranscript?.(result.text);
        return result.text;
      }

      return '';
    } catch (error) {
      console.error('Transcription failed:', error);
      setIsTranscribing(false);

      const errorMessage = error instanceof Error
        ? error.message
        : 'Transcription failed. Please try again.';

      toast.error(errorMessage);
      onDictationError?.(errorMessage);
      throw error;
    }
  }, [isRecording, stopRecording, language, onTranscript, onDictationError]);

  /**
   * Toggle recording on/off
   */
  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  /**
   * Expose imperative handle for external control
   */
  useImperativeHandle(ref, () => ({
    startRecording,
    stopRecording,
    cancelRecording,
    transcribeRecording,
    isRecording,
  }), [startRecording, stopRecording, cancelRecording, transcribeRecording, isRecording]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (disabled) return;

    switch (event.key) {
      case ' ': // Space to toggle
        event.preventDefault();
        toggleRecording();
        break;
      case 'Escape': // Escape to cancel
        if (isRecording) {
          event.preventDefault();
          cancelRecording();
        }
        break;
    }
  }, [disabled, toggleRecording, cancelRecording, isRecording]);

  // Determine button state and appearance
  const isActive = isRecording || isInitializing || isTranscribing;

  const buttonTitle = isInitializing
    ? 'Initializing microphone...'
    : isTranscribing
    ? 'Transcribing audio...'
    : isRecording
    ? 'Recording... (Click to stop)'
    : 'Start voice recording';

  const buttonClasses = cn(
    'inline-flex items-center justify-center rounded-md h-8 px-2 transition-all',
    'hover:bg-muted/50',
    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
    {
      'text-red-500 bg-red-50 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900': isRecording,
      'text-blue-500 bg-blue-50 hover:bg-blue-100 dark:bg-blue-950 dark:hover:bg-blue-900': isInitializing || isTranscribing,
      'text-muted-foreground hover:text-foreground': !isActive && !disabled,
      'opacity-50 cursor-not-allowed': disabled || !isSupported,
    },
    className
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={buttonClasses}
          onClick={toggleRecording}
          onKeyDown={handleKeyDown}
          disabled={disabled || !isSupported || isTranscribing}
          aria-pressed={isRecording}
          aria-label={buttonTitle}
          data-testid="dictation-button"
        >
          {(isInitializing || isTranscribing) && (
            <Loader2 className="inline-block size-5 animate-spin" aria-hidden="true" />
          )}
          {isRecording && !isInitializing && (
            <div className="relative">
              <Mic className="inline-block size-5" aria-hidden="true" />
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            </div>
          )}
          {!isActive && (
            <Mic className="size-4" aria-hidden="true" />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p>{buttonTitle}</p>
        {isRecording && (
          <p className="text-xs text-muted-foreground mt-1">
            Recording... Click confirm when done
          </p>
        )}
      </TooltipContent>
    </Tooltip>
  );
});
